import React from 'react';
import {
    Paper,
    Typography,
    Box,
    Grid,
    Chip,
    Card,
    Avatar,
    useTheme,
    LinearProgress
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';

interface RankedStats {
    nama: string;
    rank: string;
    poin: number;
    winRate: number;
    total: number;
    wins: number;
    losses: number;
    level: string;
    highestRank: string;
    highestPoints: number;
    photoLink: string;
    email: string;
}

interface RankedMatchStatsProps {
    stats: RankedStats;
    googlePhotoURL?: string;
}

const RankedMatchStats: React.FC<RankedMatchStatsProps> = ({ stats, googlePhotoURL }) => {
    const theme = useTheme();

    // Determine color based on rank
    const getRankColor = (rank: string) => {
        switch (rank.toLowerCase()) {
            case 'legend':
                return '#FFD700'; // Gold
            case 'champion':
                return '#FF6B6B'; // Red
            case 'true racketers':
                return '#FF8C42'; // Orange
            case 'rally machine':
                return '#4ECDC4'; // Teal
            case 'racketwood':
                return '#95E1D3'; // Light Green
            default:
                return '#7F8C8D'; // Gray (Shuttle)
        }
    };

    // Determine background color for stat cards
    const getStatCardBg = () => {
        return theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : '#ffffff';
    };

    // Use photoLink if available, otherwise use Google photo
    const photoURL = stats.photoLink || googlePhotoURL;

    return (
        <Paper
            elevation={1}
            sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : '#ffffff',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'divider' : 'rgba(0,0,0,0.12)'
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 'bold'
                }}
            >
                <EmojiEventsIcon sx={{ color: getRankColor(stats.rank) }} />
                Statistik Ranked Match
            </Typography>

            <Grid container spacing={3}>
                {/* Left side: Photo and basic info */}
                <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar
                        src={photoURL}
                        alt={stats.nama}
                        sx={{
                            width: 120,
                            height: 120,
                            mb: 2,
                            border: '3px solid',
                            borderColor: getRankColor(stats.rank)
                        }}
                    />
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
                        {stats.nama}
                    </Typography>
                    <Chip
                        label={stats.rank}
                        sx={{
                            backgroundColor: getRankColor(stats.rank),
                            color: ['legend', 'champion', 'true racketers'].includes(stats.rank.toLowerCase())
                                ? '#000'
                                : '#fff',
                            fontWeight: 'bold',
                            mb: 1
                        }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Level: {stats.level || 'N/A'}
                    </Typography>
                </Grid>

                {/* Right side: Stats */}
                <Grid item xs={12} sm={8}>
                    {/* Rating and Points */}
                    <Box sx={{ mb: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Rating Terkini
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: getRankColor(stats.rank) }}>
                                {stats.poin}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min((stats.poin / 1000) * 100, 100)}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.1)'
                                    : 'rgba(0, 0, 0, 0.15)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: getRankColor(stats.rank),
                                    borderRadius: 4
                                }
                            }}
                        />
                    </Box>

                    {/* Stats Grid */}
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        {/* Win Rate */}
                        <Grid item xs={6} sm={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    backgroundColor: getStatCardBg(),
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <TrendingUpIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: 'primary.main', flexShrink: 0 }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }}>
                                        Win Rate
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                    {stats.winRate}%
                                </Typography>
                            </Card>
                        </Grid>

                        {/* Total Games */}
                        <Grid item xs={6} sm={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    backgroundColor: getStatCardBg(),
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <AssignmentIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: 'info.main', flexShrink: 0 }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }}>
                                        Main
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                    {stats.total}
                                </Typography>
                            </Card>
                        </Grid>

                        {/* Wins */}
                        <Grid item xs={6} sm={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    backgroundColor: getStatCardBg(),
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                    Menang
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                    {stats.wins}
                                </Typography>
                            </Card>
                        </Grid>

                        {/* Losses */}
                        <Grid item xs={6} sm={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    backgroundColor: getStatCardBg(),
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                    Kalah
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                    {stats.losses}
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Highest Achievement */}
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: getStatCardBg(),
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Pencapaian Tertinggi
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: getRankColor(stats.highestRank), fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                    {stats.highestRank}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                    {stats.highestPoints} poin
                                </Typography>
                            </Box>
                            <EmojiEventsIcon
                                sx={{
                                    fontSize: { xs: 24, sm: 32 },
                                    color: getRankColor(stats.highestRank),
                                    opacity: 0.5
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default RankedMatchStats;
